import { FC, useMemo, useState } from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';

import Table from 'src/components/Table';
import useAuth from 'src/hooks/useAuth';
import { updateMemeber, updateUserPermissions } from 'src/apis';
import { Member, UserPermissions } from 'src/types';
import { getUserPermissions } from 'src/utils/permissions';
import useNotification from 'src/hooks/useNotification';

import AdditionalPermissions from './AdditionalPermissions';

interface TeamMembersProps {
  members: Member[];
  onRefetch: VoidFunction;
}

const Permissions: Record<string, string> = {
  view: 'View Only',
  staff: 'Edit',
  admin: 'Admin',
  super: 'Super Admin',
};

const TeamMembers: FC<TeamMembersProps> = ({ members, onRefetch }) => {
  const [loading, setLoading] = useState<string>('');
  const { user, fetchUser } = useAuth();
  const { handleSuccess, handleError } = useNotification();
  const isAdmin = user?.role === 'admin' || user?.role === 'super';

  const handlePermissionsSave = async (
    member: Member,
    next: UserPermissions
  ) => {
    setLoading(member.id);
    try {
      await updateUserPermissions(member.id, next);
      await onRefetch();
      if (member.id === user?.id) {
        await fetchUser();
      }
      handleSuccess('Additional permissions updated successfully.');
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setLoading('');
    }
  };

  const headers = [
    { field: 'name', label: 'Name' },
    { field: 'email', label: 'Email Address' },
    { field: 'role', label: 'Permissions' },
    { field: 'additionalPermissions', label: 'Additional Perms' },
  ];

  const membersData = useMemo(() => {
    return members.map((member) => ({
      ...member,
      name: [member.firstName, member.lastName].join(' '),
      role:
        isAdmin && member.role !== 'super' ? (
          <Select
            id='permission-select'
            value={member.role}
            onChange={async (event) => {
              setLoading(member.id);
              await updateMemeber(member.id, {
                email: member.email,
                firstName: member.firstName,
                lastName: member.lastName,
                password: member.password,
                organizationId: member.organizationId,
                enabled: true,
                role: event.target.value,
              });
              await onRefetch();
              setLoading('');
            }}
            style={{
              minWidth: 150,
            }}
            disabled={member.id === loading}
          >
            <MenuItem value='view'>View Only</MenuItem>
            <MenuItem value='staff'>Edit</MenuItem>
            <MenuItem value='admin'>Admin</MenuItem>
          </Select>
        ) : (
          member.role && <Typography>{Permissions[member.role]}</Typography>
        ),
      additionalPermissions: (
        <AdditionalPermissions
          value={getUserPermissions(member)}
          disabled={!isAdmin || member.role === 'super'}
          saving={member.id === loading}
          onSave={(next) => handlePermissionsSave(member, next)}
        />
      ),
    }));
  }, [members, isAdmin, loading, onRefetch, user?.id, fetchUser]);

  return <Table headers={headers} data={membersData} />;
};

export default TeamMembers;
